import React, { useMemo, useRef } from 'react';
import MaterialTable, {
    materialTableHeight,
    TableActions,
} from '@components/molecules/MaterialTable';
import { Column, Options } from '@material-table/core';
import { readableDateFromDate, readableTimeFromDate, sortByDate } from '@utils/date-utils';
import EventIcon from '@mui/icons-material/Event';
import { EventRoleStatus, WeighInItem } from '@type/db-types';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import useModalControl from '@hooks/useModalControl';
import { EventContextValues } from '@hooks/useEventContext';
import { EventRoleWithMemberships } from '@modules/supabase/events';
import { eventFromRow } from '@utils/event-utils';

interface Props {
    events: EventRoleWithMemberships[] | null;
    loading: boolean;
    onSelectEvent: (event: EventContextValues['event']) => void;
    setViewType: (viewType: 'table' | 'card') => void;
    columns?: Column<EventRoleWithMemberships>[];
}

export default function EventsTable({
    loading,
    events,
    onSelectEvent,
    setViewType,
    columns = [],
}: Props) {
    const { setShowEventModal } = useModalControl();
    const tableRef: React.MutableRefObject<undefined | unknown> = useRef();

    const actions: TableActions<EventRoleStatus> = useMemo(
        () => [
            // {
            //     icon: EditIcon,
            //     tooltip: 'Edit Event',
            //     isFreeAction: false,
            //     onClick: (_event: unknown, data: Event | Event[]) =>
            //         navigate(`/events/${eventFromRow(data)?.id}`),
            // },
            // {
            //     icon: PlaylistAddIcon,
            //     tooltip: 'Join Event',
            //     isFreeAction: false,
            //     onClick: (_event: unknown, data: Event | Event[]) =>
            //         onSelectEvent(eventFromRow(data)),
            // },
            {
                icon: ViewModuleIcon,
                tooltip: 'Change View',
                isFreeAction: true,
                onClick: () => setViewType('card'),
            },
            // {
            //     icon: InfoIcon,
            //     tooltip: 'View Event',
            //     isFreeAction: false,
            //     onClick: (_event: unknown, data: EventRoleStatus | EventRoleStatus[]) =>
            //         onSelectEvent(eventFromRow(data)),
            // },
            {
                icon: EventIcon,
                tooltip: 'Add Event',
                isFreeAction: true,
                onClick: () => setShowEventModal(true),
            },
        ],

        [setShowEventModal, setViewType],
    );

    return (
        <MaterialTable<EventRoleWithMemberships>
            tableRef={tableRef}
            actions={actions}
            columns={[...COLUMNS, ...columns]}
            data={events || []}
            onRowClick={(_e, rowData) => rowData && onSelectEvent(eventFromRow(rowData))}
            localization={{
                toolbar: {
                    nRowsSelected: (number) =>
                        `${number} wrestler${number === 1 ? '' : 's'} selected`,
                },
            }}
            loading={loading}
            options={TABLE_OPTIONS}
            tableId="events-table"
            title="Events"
            style={{ flex: 1 }}
        />
    );
}

const TABLE_OPTIONS: Options<EventRoleWithMemberships> = {
    filtering: false,
    rowStyle() {
        return {
            // background: `linear-gradient(${index % 2 === 0 ? '50deg' : '220deg'}, #f0f9fa 0%, transparent 100%)`,
            // background: `radial-gradient( #d0edf099,#d0edf099,#d0edf099,#f0f9fa20)`,
            background: `linear-gradient(170deg, #d0edf099 0%, transparent 100%)`,
            height: '8rem',
            borderBottom: '0.5rem solid white',
        };
    },
    actionsColumnIndex: -1,
    maxBodyHeight: `${materialTableHeight}vh`,
    minBodyHeight: `${materialTableHeight}vh`,
};

const COLUMNS: Column<EventRoleWithMemberships>[] = [
    {
        title: 'Image',
        field: 'imageUrl',
        render: ({ imageUrl }) => <img src={imageUrl} alt="" style={{ maxWidth: '12rem' }} />,
    },
    { title: 'Name', field: 'name' },
    {
        title: 'Location',
        field: 'location',
        render: ({ location }) => location.replace(', USA', ''),
    },
    {
        title: 'Date',
        field: 'weighInAt',
        render: ({ startAt }) => (startAt ? readableDateFromDate(startAt) : startAt),
    },
    {
        hidden: true,
        title: 'Weigh Ins Start',
        field: 'weighInAt',
        render: ({ weighInAt }) =>
            weighInAt
                ? readableTimeFromDate(
                      (weighInAt as WeighInItem[]).sort((a, b) =>
                          sortByDate(b.weighInAtDateTime, a.weighInAtDateTime),
                      )[0].weighInAtDateTime,
                  )
                : weighInAt,
    },
    {
        title: 'Wrestling Start',
        field: 'startAt',
        render: ({ startAt }) => (startAt ? readableTimeFromDate(startAt) : startAt),
    },
    { title: 'Registrants', field: 'registrantsCount' },
    { hidden: true, title: 'Fee', field: 'fee', type: 'currency' },
    { hidden: true, title: 'Is Private', field: 'isPrivate' },

    {
        title: 'Created At',
        hidden: true,
        field: 'createdAt',
        render: ({ createdAt }) => (createdAt ? readableDateFromDate(createdAt) : createdAt),
        type: 'numeric',
    },
];
